import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import { Shield, Database, Lock, Cookie, UserCheck, Mail, Phone, Home as HomeIcon } from "lucide-react";

export default function PrivacyPolicy() {
  const sections = [
    {
      id: "information-collection",
      title: "Information Collection",
      icon: Database,
      items: [
        "We collect personal details such as your name, email address, phone number, and payment details when you make a booking or contact us.",
        "We may collect information about your interactions with our website, including IP addresses, browser types, and device information.",
        "If you opt-in to receive promotional content, we may collect additional information regarding your preferences."
      ]
    },
    {
      id: "use-of-information",
      title: "Use of Information",
      icon: UserCheck,
      items: [
        "Your information is used to process bookings, provide customer support, and enhance your experience at WyldWoods.",
        "We may use your details to send booking confirmations, updates, and promotional offers if you have opted to receive them.",
        "Your payment details are processed securely through Razorpay, and we do not store your payment credentials."
      ]
    },
    {
      id: "data-protection",
      title: "Data Protection",
      icon: Lock,
      items: [
        "We implement appropriate security measures to protect your personal information against unauthorized access or disclosure.",
        "We do not sell, rent, or share your personal data with third parties except for payment processing and legal compliance."
      ]
    },
    {
      id: "cookies-tracking",
      title: "Cookies and Tracking Technologies",
      icon: Cookie,
      items: [
        "Our website may use cookies to enhance user experience and track visitor interactions.",
        "You can manage cookie preferences through your browser settings."
      ]
    },
    {
      id: "your-rights",
      title: "Your Rights",
      icon: Shield,
      items: [
        "You can request access to, correction, or deletion of your personal data at any time by contacting us at support@wyldwoods.in."
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Header />
      <PageBanner
        title="Privacy Policy"
        description="Your privacy is our priority"
        backgroundImage="/assets/banner-bg.jpg"
      />
      
      <main className="flex-grow">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Introduction */}
          <div className="bg-gradient-to-r from-[#E67E22]/10 to-[#F39C12]/10 border-l-4 border-[#E67E22] rounded-lg p-6 mb-12">
            <div className="flex items-start">
              <Shield className="text-[#E67E22] mr-4 flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="font-serif text-xl font-bold text-[#2C3E50] mb-2">
                  Committed to Your Privacy
                </h2>
                <p className="text-[#555] leading-relaxed">
                  At WyldWoods Rural Retreat, we value your privacy and are committed to protecting your personal information. 
                  This Privacy Policy outlines the types of data we collect, how we use it, and the measures we take to ensure its security.
                </p>
              </div>
            </div>
          </div>

          {/* Privacy Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div
                key={section.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 p-8 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#E67E22] to-[#F39C12] flex items-center justify-center mr-4">
                    <section.icon className="text-white" size={24} />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-[#2C3E50]">
                    {index + 1}. {section.title}
                  </h3>
                </div>
                <ul className="space-y-3 ml-16">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start group">
                      <span className="inline-block w-2 h-2 rounded-full bg-[#E67E22] mr-3 mt-2 flex-shrink-0 group-hover:scale-125 transition-transform"></span>
                      <span className="text-[#555] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="mt-12 bg-gradient-to-r from-[#2C3E50] to-[#34495E] text-white rounded-xl p-8">
            <h3 className="font-serif text-2xl font-bold mb-4 text-center">
              6. Contact
            </h3>
            <p className="text-center text-gray-300 mb-6">
              For any privacy-related concerns, reach us at:
            </p>
            
            <div className="max-w-md mx-auto space-y-4">
              <div className="flex items-center justify-center group">
                <div className="w-10 h-10 rounded-lg bg-[#E67E22]/20 flex items-center justify-center mr-3 group-hover:bg-[#E67E22] transition-colors duration-300">
                  <Mail size={18} className="text-[#E67E22] group-hover:text-white transition-colors" />
                </div>
                <a 
                  href="mailto:support@wyldwoods.in" 
                  className="text-[#F39C12] hover:text-[#E67E22] transition-colors font-medium"
                >
                  support@wyldwoods.in
                </a>
              </div>
              
              <div className="flex items-center justify-center group">
                <div className="w-10 h-10 rounded-lg bg-[#E67E22]/20 flex items-center justify-center mr-3 group-hover:bg-[#E67E22] transition-colors duration-300">
                  <Phone size={18} className="text-[#E67E22] group-hover:text-white transition-colors" />
                </div>
                <a href="tel:+919445771282" className="text-gray-300 hover:text-white transition-colors">
                  +91 94457 71282
                </a>
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-8 bg-white rounded-xl p-6 text-center border-2 border-[#E67E22]/20">
            <p className="text-[#555] leading-relaxed">
              We regularly review our Privacy Policy to ensure compliance with applicable laws and regulations. 
              By using our services, you consent to the practices described in this policy.
            </p>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center text-[#E67E22] hover:text-[#F39C12] font-semibold transition-colors"
            >
              <HomeIcon size={20} className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
